import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Layout, Brand } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { usePatientMode } from "@/lib/patientMode";
import { ModuleIntro, ModuleBanner } from "@/components/ModuleIntro";
import {
  EMPATHIE_DEFINITION,
  EMPATHIE_CONSIGNE,
  EMPATHIE_DIMENSIONS,
  EMPATHIE_GUIDE,
  FRUSTRATION_SECTIONS,
  EMPATHIE_T,
} from "@/data/empathie";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const emptyDims = () => Object.fromEntries(EMPATHIE_DIMENSIONS.map((d) => [d.key, { guess: "", reality: "" }]));
const fallback = (n, d) => (n && n.trim() ? n.trim() : d);

const Textarea = (props) => (
  <textarea rows={3} {...props} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y" />
);
const Primary = ({ children, ...p }) => (
  <button {...p} className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{children}</button>
);
const Ghost = ({ children, ...p }) => (
  <button {...p} className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{children}</button>
);

const Welcome = ({ t, lang, onStart, onConnais, onJoinCode }) => (
  <div className="animate-fade-up text-center">
    <ModuleBanner moduleKey="empathie" />
    <h1 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl text-[var(--gold)] leading-tight mb-6">{t.welcomeTitle}</h1>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-6">{EMPATHIE_DEFINITION[lang]}</p>
    <ModuleIntro moduleKey="empathie" lang={lang} className="mb-8" />
    <Primary onClick={onStart} data-testid="empathie-btn-start">{t.start}</Primary>
    <div className="mt-4">
      <button type="button" onClick={onJoinCode} data-testid="empathie-have-code" className="text-sm text-[var(--ink-soft)] underline decoration-[var(--gold-light)] underline-offset-4 hover:text-[var(--gold)] transition-colors">{t.haveCode}</button>
    </div>

    <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-6 sm:p-7 mt-10 text-center" data-testid="empathie-connais-cta-welcome">
      <p className="text-xs uppercase tracking-[0.15em] text-[var(--gold-light)] mb-1">{t.optionalExercise}</p>
      <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Est-ce que je te connais vraiment ?</h3>
      <p className="text-[var(--ink)] mb-4 max-w-xl mx-auto">{t.connaisBefore}</p>
      <Ghost onClick={onConnais} data-testid="empathie-open-connais-welcome">{t.connaisStart}</Ghost>
    </div>
  </div>
);

const Consigne = ({ t, lang, onPrev, onNext }) => (
  <div className="animate-fade-up">
    <img src="/couple/empathie-inside.jpg" alt="" className="w-full h-40 sm:h-52 object-cover rounded-3xl mb-6" data-testid="empathie-consigne-photo" />
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-2 text-center">{t.ex1}</h2>
    <p className="text-center text-[var(--ink-soft)] mb-6">{t.ex1Sub}</p>
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 text-[var(--ink)] leading-relaxed space-y-3">
      {EMPATHIE_CONSIGNE[lang].map((p, i) => (<p key={i}>{p}</p>))}
    </div>
    <div className="flex items-center justify-between mt-8">
      <Ghost onClick={onPrev}>{t.prev}</Ghost>
      <Primary onClick={onNext} data-testid="empathie-btn-consigne-next">{t.next}</Primary>
    </div>
  </div>
);

const ModeChoice = ({ t, onSingle, onTwo, onPrev }) => (
  <div className="animate-fade-up">
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.modeTitle}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <button onClick={onSingle} data-testid="empathie-mode-single" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.modeSingle}</h3>
        <p className="text-sm text-[var(--ink-soft)]">{t.modeSingleDesc}</p>
      </button>
      <button onClick={onTwo} data-testid="empathie-mode-two" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.modeTwo}</h3>
        <p className="text-sm text-[var(--ink-soft)]">{t.modeTwoDesc}</p>
      </button>
    </div>
    <div className="mt-8"><Ghost onClick={onPrev}>{t.prev}</Ghost></div>
  </div>
);

const Setup = ({ t, p1Name, p2Name, setP1Name, setP2Name, onPrev, onNext }) => (
  <div className="animate-fade-up">
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.setupTitle}</h2>
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 space-y-5">
      <p className="text-sm text-[var(--ink-soft)]">{t.setupHint}</p>
      <div>
        <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.partner1}</label>
        <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} data-testid="empathie-p1-name" placeholder={t.partner1Ph} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.partner2}</label>
        <input value={p2Name} onChange={(e) => setP2Name(e.target.value)} data-testid="empathie-p2-name" placeholder={t.partner2Ph} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
      </div>
    </div>
    <div className="flex items-center justify-between mt-8">
      <Ghost onClick={onPrev}>{t.prev}</Ghost>
      <Primary onClick={onNext} data-testid="empathie-btn-setup-next">{t.setupStart}</Primary>
    </div>
  </div>
);

const PartnerForm = ({ t, lang, who, name, otherName, dims, onChange, onPrev, onNext, nextLabel }) => (
  <div className="animate-fade-up">
    <div className="rounded-3xl bg-[var(--gold)] text-white p-5 sm:p-6 mb-6 text-center">
      <p className="text-xs uppercase tracking-[0.2em] text-white/80 mb-1">{t.sheetOf}</p>
      <h2 className="font-serif-title text-3xl">{name}</h2>
      <p className="text-sm text-white/90 mt-2">{t.sheetHint(otherName)}</p>
    </div>
    <div className="space-y-6">
      {EMPATHIE_DIMENSIONS.map((d) => (
        <div key={d.key} className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7" data-testid={`empathie-dim-${who}-${d.key}`}>
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">{d.title[lang]}</h3>
          <p className="text-sm text-[var(--ink-soft)] mb-4">{d.intro[lang]}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[var(--ink)] mb-1">{d.guessLabel[lang]}</label>
              <p className="text-[0.7rem] text-[var(--ink-soft)] mb-1.5">{d.hint[lang]}</p>
              <Textarea value={dims[d.key].guess} onChange={(e) => onChange(d.key, "guess", e.target.value)} data-testid={`empathie-${who}-${d.key}-guess`} placeholder={t.guessPh} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[var(--ink)] mb-1">{d.realityLabel[lang]}</label>
              <p className="text-[0.7rem] text-[var(--ink-soft)] mb-1.5">{t.realityHint}</p>
              <Textarea value={dims[d.key].reality} onChange={(e) => onChange(d.key, "reality", e.target.value)} data-testid={`empathie-${who}-${d.key}-reality`} placeholder={t.realityPh} />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between mt-8 no-print">
      <Ghost onClick={onPrev} data-testid={`empathie-btn-prev-${who}`}>{t.prev}</Ghost>
      <Primary onClick={onNext} data-testid={`empathie-btn-next-${who}`}>{nextLabel}</Primary>
    </div>
  </div>
);

const Handoff = ({ t, nextName, onContinue }) => (
  <div className="animate-fade-up text-center">
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12">
      <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-4">{t.handoffTitle(nextName)}</h2>
      <p className="text-[var(--ink)] leading-relaxed max-w-md mx-auto mb-8">{t.handoffText(nextName)}</p>
      <Primary onClick={onContinue} data-testid="empathie-btn-handoff">{t.handoffBtn(nextName)}</Primary>
    </div>
  </div>
);

/* ---- Deux appareils ---- */
const TwoRole = ({ t, onCreate, onJoin, onPrev }) => (
  <div className="animate-fade-up">
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.modeTwo}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <button onClick={onCreate} data-testid="empathie-td-create" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.createTitle}</h3>
        <p className="text-sm text-[var(--ink-soft)]">{t.createDesc}</p>
      </button>
      <button onClick={onJoin} data-testid="empathie-td-join" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.joinTitle}</h3>
        <p className="text-sm text-[var(--ink-soft)]">{t.joinDesc}</p>
      </button>
    </div>
    <div className="mt-8"><Ghost onClick={onPrev}>{t.prev}</Ghost></div>
  </div>
);

const NameGate = ({ t, title, value, setValue, code, setCode, showCode, onPrev, onNext, ctaTestId, cta }) => (
  <div className="animate-fade-up">
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{title}</h2>
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 space-y-5">
      {showCode && (
        <div>
          <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.codeLabel}</label>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="empathie-join-code" placeholder={t.codePh} maxLength={6} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm tracking-[0.3em] font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.yourName}</label>
        <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="empathie-td-name" placeholder={t.yourNamePh} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
      </div>
    </div>
    <div className="flex items-center justify-between mt-8">
      <Ghost onClick={onPrev}>{t.prev}</Ghost>
      <Primary onClick={onNext} data-testid={ctaTestId}>{cta}</Primary>
    </div>
  </div>
);

const CodeScreen = ({ t, code, onFill }) => {
  const { patientMode } = usePatientMode();
  const joinUrl = `${window.location.origin}${patientMode ? "/p/empathie" : "/empathie"}?code=${code}`;
  return (
    <div className="animate-fade-up text-center">
      <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-4">{t.yourCode}</h2>
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 mb-6">
        <p className="text-sm text-[var(--ink-soft)] mb-3">{t.shareCode}</p>
        <div className="font-mono text-5xl tracking-[0.4em] text-[var(--gold)] font-bold my-4" data-testid="empathie-code-value">{code}</div>
        <div className="flex justify-center my-4" data-testid="empathie-qr">
          <div className="bg-white p-3 rounded-2xl border border-[var(--border)]">
            <QRCodeSVG value={joinUrl} size={148} bgColor="#ffffff" fgColor="#4A3B36" level="M" />
          </div>
        </div>
        <button onClick={() => { navigator.clipboard.writeText(code).then(() => toast.success(t.codeCopied)).catch(() => {}); }} data-testid="empathie-code-copy" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)]">{t.copyCode}</button>
      </div>
      <Primary onClick={onFill} data-testid="empathie-code-fill">{t.fillSheet}</Primary>
    </div>
  );
};

const WaitScreen = ({ t, code, role, onReady }) => {
  const [status, setStatus] = useState({ p1_submitted: false, p2_submitted: false });
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const data = await axios.get(`${API}/empathie/seances/${code}`).then((r) => r.data);
        if (!active) return;
        setStatus(data);
        if (data.both_submitted) { active = false; onReady(data); }
      } catch { /* réseau */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { active = false; clearInterval(id); };
  }, [code, onReady]);

  const mine = role === "p1" ? status.p1_submitted : status.p2_submitted;
  const other = role === "p1" ? status.p2_submitted : status.p1_submitted;

  return (
    <div className="animate-fade-up text-center" data-testid="empathie-wait">
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12">
        <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-4">{t.waitTitle}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-md mx-auto mb-6">{t.waitText}</p>
        <p className="text-sm text-[var(--ink-soft)] mb-2">{t.codeLabel} : <span className="font-mono tracking-widest text-[var(--gold)]">{code}</span></p>
        <div className="inline-flex gap-4 text-sm">
          <span className={mine ? "text-[var(--gold)]" : "text-[var(--ink-soft)]"}>{t.you} : {mine ? t.done : "…"}</span>
          <span className={other ? "text-[var(--gold)]" : "text-[var(--ink-soft)]"}>{t.partner} : {other ? t.done : t.inProgress}</span>
        </div>
      </div>
    </div>
  );
};

const ComparePair = ({ t, subjectName, observerName, reality, guess }) => (
  <div className="rounded-2xl bg-white/60 border border-[var(--border)] p-4">
    <p className="text-sm font-semibold text-[var(--gold)] mb-3">{t.seenBy(subjectName, observerName)}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="rounded-xl bg-[var(--sand)] p-3">
        <p className="text-xs font-semibold text-[var(--ink)] mb-1">{t.realityOf(subjectName)}</p>
        <p className="text-sm text-[var(--ink-soft)] whitespace-pre-wrap">{reality || "—"}</p>
      </div>
      <div className="rounded-xl bg-[var(--sand)] p-3">
        <p className="text-xs font-semibold text-[var(--ink)] mb-1">{t.guessedBy(observerName)}</p>
        <p className="text-sm text-[var(--ink-soft)] whitespace-pre-wrap">{guess || "—"}</p>
      </div>
    </div>
  </div>
);

const hasFrust = (data) => data && Object.values(data).some((v) => v && String(v).trim());

const FrustSummary = ({ lang, title, data }) => (
  <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7 print-break" data-testid="empathie-frust-summary">
    <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{title}</h3>
    <div className="space-y-4">
      {FRUSTRATION_SECTIONS.map((section) => {
        const filled = section.fields.filter((f) => data[f.key] && data[f.key].trim());
        if (!filled.length) return null;
        return (
          <div key={section.title[lang]}>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--gold)] mb-2">{section.title[lang]}</p>
            <div className="space-y-2">
              {filled.map((f) => (
                <div key={f.key} className="rounded-xl bg-white/60 border border-[var(--border)] p-3">
                  <p className="text-xs font-semibold text-[var(--ink)] mb-1">{f.label[lang]}</p>
                  <p className="text-sm text-[var(--ink-soft)] whitespace-pre-wrap">{data[f.key]}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Central = ({ t, lang, p1, p2, mode, frustSingle, frustP1, frustP2, onRestart, onFrustration, onRefresh, onConnais }) => {
  const n1 = fallback(p1.name, t.partner1);
  const n2 = fallback(p2.name, t.partner2);
  const dget = (p, k) => (p.dims && p.dims[k]) || { guess: "", reality: "" };
  const anyFrust = mode === "two" ? (hasFrust(frustP1) || hasFrust(frustP2)) : hasFrust(frustSingle);
  return (
    <div className="animate-fade-up print-container" data-testid="empathie-central">
      <div className="hidden print:block mb-6"><Brand /></div>
      <div className="text-center mb-10">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{t.centralTitle}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.centralText}</p>
      </div>
      <div className="space-y-6">
        {EMPATHIE_DIMENSIONS.map((d) => (
          <div key={d.key} className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7 print-break" data-testid={`empathie-central-${d.key}`}>
            <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{d.title[lang]}</h3>
            <div className="space-y-4">
              <ComparePair t={t} subjectName={n1} observerName={n2} reality={dget(p1, d.key).reality} guess={dget(p2, d.key).guess} />
              <ComparePair t={t} subjectName={n2} observerName={n1} reality={dget(p2, d.key).reality} guess={dget(p1, d.key).guess} />
            </div>
            <div className="mt-4 rounded-2xl border border-dashed border-[var(--gold-light)] bg-white/40 p-4" data-testid={`empathie-guide-${d.key}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--gold)] mb-2">{t.guideTitle}</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-[var(--ink)]">
                {EMPATHIE_GUIDE[lang][d.key].map((q) => (<li key={q}>{q}</li>))}
              </ul>
            </div>
          </div>
        ))}

        {anyFrust && (
          <div className="pt-2" data-testid="empathie-report-frustration">
            <h2 className="font-serif-title text-3xl text-[var(--gold)] text-center mb-6">{t.frustReportTitle}</h2>
            <div className="space-y-6">
              {mode === "two" ? (
                <>
                  {hasFrust(frustP1) && <FrustSummary lang={lang} title={t.frustFor(n1)} data={frustP1} />}
                  {hasFrust(frustP2) && <FrustSummary lang={lang} title={t.frustFor(n2)} data={frustP2} />}
                </>
              ) : (
                hasFrust(frustSingle) && <FrustSummary lang={lang} title={t.frustMgmt} data={frustSingle} />
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-7 mt-8 text-center no-print">
        <p className="text-[var(--ink)] mb-4">{t.exploreFrust}{mode === "two" ? t.exploreFrustTwoSuffix : ""}.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Primary onClick={onFrustration} data-testid="empathie-btn-frustration">{anyFrust ? t.frustEdit : t.frustDeepen}</Primary>
          {mode === "two" && <Ghost onClick={onRefresh} data-testid="empathie-btn-refresh">{t.refresh}</Ghost>}
        </div>
      </div>

      <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-6 sm:p-7 mt-5 text-center no-print" data-testid="empathie-connais-cta">
        <p className="text-xs uppercase tracking-[0.15em] text-[var(--gold-light)] mb-1">{t.optionalExercise}</p>
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Est-ce que je te connais vraiment ?</h3>
        <p className="text-[var(--ink)] mb-4 max-w-xl mx-auto">{t.connaisAfter}</p>
        <Primary onClick={onConnais} data-testid="empathie-open-connais">{t.connaisStart}</Primary>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 mt-8 no-print">
        <Primary onClick={() => window.print()} data-testid="empathie-btn-export-pdf">{t.exportFull}</Primary>
        <Ghost onClick={onRestart} data-testid="empathie-btn-restart">{t.restart}</Ghost>
      </div>
    </div>
  );
};

const FrustrationWorksheet = ({ t, lang, data, onChange, onSave, onBack }) => (
  <div className="animate-fade-up print-container" data-testid="empathie-frustration">
    <div className="hidden print:block mb-6"><Brand /></div>
    <div className="text-center mb-8">
      <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">{t.frustMgmt}</h2>
      <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.frustIntro}</p>
    </div>
    <div className="space-y-6">
      {FRUSTRATION_SECTIONS.map((section) => (
        <div key={section.title[lang]} className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7 print-break" data-testid={`empathie-frust-${section.fields[0].key}`}>
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{section.title[lang]}</h3>
          <div className="space-y-4">
            {section.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-semibold text-[var(--ink)] mb-1.5">{f.label[lang]}</label>
                <Textarea value={data[f.key] || ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`empathie-frust-${f.key}`} placeholder={t.answerPh} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <div className="flex flex-wrap items-center justify-center gap-4 mt-8 no-print">
      <Primary onClick={onSave} data-testid="empathie-frust-save">{t.saveSeeReport}</Primary>
      <Ghost onClick={onBack} data-testid="empathie-frust-back">{t.backNoSave}</Ghost>
    </div>
  </div>
);

export default function EmpathieModule({ initialLang }) {
  const { patientMode } = usePatientMode();
  const navigate = useNavigate();
  const [lang, setLang] = useState(initialLang || "fr");
  const [phase, setPhase] = useState("welcome");
  const [mode, setMode] = useState("single");
  // single
  const [p1Name, setP1Name] = useState("");
  const [p2Name, setP2Name] = useState("");
  const [p1Dims, setP1Dims] = useState(emptyDims);
  const [p2Dims, setP2Dims] = useState(emptyDims);
  // two devices
  const [role, setRole] = useState("p1");
  const [code, setCode] = useState("");
  const [myName, setMyName] = useState("");
  const [myDims, setMyDims] = useState(emptyDims);
  const [result, setResult] = useState(null); // {p1,p2}
  // frustration
  const [myFrust, setMyFrust] = useState({});
  const [frustP1, setFrustP1] = useState({});
  const [frustP2, setFrustP2] = useState({});
  const [searchParams] = useSearchParams();
  const t = EMPATHIE_T[lang];

  // Rejoindre via QR code (URL ?code=XXXXXX)
  useEffect(() => {
    const c = searchParams.get("code");
    if (c) { setMode("two"); setRole("p2"); setCode(c.toUpperCase()); setPhase("td_join"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const name1 = fallback(p1Name, t.partner1);
  const name2 = fallback(p2Name, t.partner2);
  const go = (p) => { setPhase(p); scrollTop(); };

  const changeP1 = (dim, f, v) => setP1Dims((prev) => ({ ...prev, [dim]: { ...prev[dim], [f]: v } }));
  const changeP2 = (dim, f, v) => setP2Dims((prev) => ({ ...prev, [dim]: { ...prev[dim], [f]: v } }));
  const changeMy = (dim, f, v) => setMyDims((prev) => ({ ...prev, [dim]: { ...prev[dim], [f]: v } }));
  const changeMyFrust = (k, v) => setMyFrust((prev) => ({ ...prev, [k]: v }));

  const finishSingle = () => {
    axios.post(`${API}/empathie-evaluations`, { p1: { name: name1, dims: p1Dims }, p2: { name: name2, dims: p2Dims } }).catch((e) => console.error(e));
    setResult({ p1: { name: name1, dims: p1Dims }, p2: { name: name2, dims: p2Dims } });
    go("central");
  };

  const createSeance = async () => {
    try {
      const { code: c } = await axios.post(`${API}/empathie/seances`).then((r) => r.data);
      setCode(c); setRole("p1"); go("td_code");
    } catch { toast.error(t.createFail); }
  };

  const joinSeance = async () => {
    if (code.trim().length < 4) { toast.error(t.enterValidCode); return; }
    try {
      const data = await axios.get(`${API}/empathie/seances/${code.trim()}`).then((r) => r.data);
      if (!data.found) { toast.error(t.codeNotFound); return; }
      setRole("p2"); go("td_fill");
    } catch { toast.error(t.joinError); }
  };

  const submitTwo = async () => {
    try {
      const out = await axios.post(`${API}/empathie/seances/${code}/submit`, { role, name: myName, dims: myDims }).then((r) => r.data);
      setFrustP1(out.p1_frust || {}); setFrustP2(out.p2_frust || {});
      if (out.both_submitted) { setResult({ p1: out.p1, p2: out.p2 }); go("central"); }
      else go("td_wait");
    } catch { toast.error(t.submitFail); }
  };

  const refreshSeance = async () => {
    try {
      const data = await axios.get(`${API}/empathie/seances/${code}`).then((r) => r.data);
      setResult({ p1: data.p1, p2: data.p2 });
      setFrustP1(data.p1_frust || {}); setFrustP2(data.p2_frust || {});
      toast.success(t.reportRefreshed);
    } catch { toast.error(t.refreshFail); }
  };

  const saveFrust = async () => {
    if (mode === "two") {
      try {
        const out = await axios.post(`${API}/empathie/seances/${code}/frustration`, { role, frust: myFrust }).then((r) => r.data);
        setResult({ p1: out.p1, p2: out.p2 });
        setFrustP1(out.p1_frust || {}); setFrustP2(out.p2_frust || {});
      } catch {
        toast.error(t.saveFail);
        return; // on reste sur la feuille pour ne pas perdre les réponses
      }
    }
    go("central");
  };

  const restart = () => {
    setP1Name(""); setP2Name(""); setP1Dims(emptyDims()); setP2Dims(emptyDims());
    setRole("p1"); setCode(""); setMyName(""); setMyDims(emptyDims()); setResult(null);
    setMyFrust({}); setFrustP1({}); setFrustP2({});
    setMode("single"); go("welcome");
  };

  const central = result || { p1: { name: name1, dims: p1Dims }, p2: { name: name2, dims: p2Dims } };

  const render = () => {
    switch (phase) {
      case "welcome": return <Welcome t={t} lang={lang} onStart={() => go("consigne")} onConnais={() => navigate(patientMode ? `/p/connais?lang=${lang}` : "/connais")} onJoinCode={() => { setMode("two"); setRole("p2"); go("td_join"); }} />;
      case "consigne": return <Consigne t={t} lang={lang} onPrev={() => go("welcome")} onNext={() => go("mode")} />;
      case "mode": return <ModeChoice t={t} onPrev={() => go("consigne")} onSingle={() => { setMode("single"); go("setup"); }} onTwo={() => { setMode("two"); go("td_role"); }} />;
      // single
      case "setup": return <Setup t={t} {...{ p1Name, p2Name, setP1Name, setP2Name }} onPrev={() => go("mode")} onNext={() => go("p1")} />;
      case "p1": return <PartnerForm t={t} lang={lang} who="p1" name={name1} otherName={name2} dims={p1Dims} onChange={changeP1} onPrev={() => go("setup")} onNext={() => go("handoff")} nextLabel={t.toPartner2} />;
      case "handoff": return <Handoff t={t} nextName={name2} onContinue={() => go("p2")} />;
      case "p2": return <PartnerForm t={t} lang={lang} who="p2" name={name2} otherName={name1} dims={p2Dims} onChange={changeP2} onPrev={() => go("p1")} onNext={finishSingle} nextLabel={t.seeCentral} />;
      // two devices
      case "td_role": return <TwoRole t={t} onPrev={() => go("mode")} onCreate={() => go("td_create")} onJoin={() => go("td_join")} />;
      case "td_create": return <NameGate t={t} title={t.createTitle} value={myName} setValue={setMyName} code={code} setCode={setCode} showCode={false} onPrev={() => go("td_role")} onNext={createSeance} ctaTestId="empathie-create-submit" cta={t.genCode} />;
      case "td_code": return <CodeScreen t={t} code={code} onFill={() => go("td_fill")} />;
      case "td_join": return <NameGate t={t} title={t.joinTitle} value={myName} setValue={setMyName} code={code} setCode={setCode} showCode onPrev={() => go("td_role")} onNext={joinSeance} ctaTestId="empathie-join-submit" cta={t.join} />;
      case "td_fill": return <PartnerForm t={t} lang={lang} who={role} name={fallback(myName, t.you)} otherName={t.partner.toLowerCase()} dims={myDims} onChange={changeMy} onPrev={() => go(role === "p1" ? "td_code" : "td_join")} onNext={submitTwo} nextLabel={t.validateSheet} />;
      case "td_wait": return <WaitScreen t={t} code={code} role={role} onReady={(d) => { setResult({ p1: d.p1, p2: d.p2 }); setFrustP1(d.p1_frust || {}); setFrustP2(d.p2_frust || {}); go("central"); }} />;
      // shared
      case "frustration": return <FrustrationWorksheet t={t} lang={lang} data={myFrust} onChange={changeMyFrust} onSave={saveFrust} onBack={() => go("central")} />;
      default: return <Central t={t} lang={lang} p1={central.p1} p2={central.p2} mode={mode} frustSingle={myFrust} frustP1={frustP1} frustP2={frustP2} onRestart={restart} onFrustration={() => go("frustration")} onRefresh={refreshSeance} onConnais={() => navigate(patientMode ? `/p/connais?lang=${lang}` : "/connais")} />;
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-4 no-print">
        {patientMode ? <span /> : <Link to="/" data-testid="empathie-home-link" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{t.allModules}</Link>}
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      {render()}
    </Layout>
  );
}
